/**
 * GA4 event payload builders for the Contact Builder lead-form flow (NHS20-8253).
 *
 * Pure functions — no Redux, no DOM, no dispatch. Each returns a `{ eventName,
 * customProperties }` shape ready to spread into `googleAnalytics4Event(...)`. Property names
 * (including `preferd_method`) match the Jira NHS20-8253 acceptance-criteria table exactly —
 * not a typo to fix. `position` is provided by the caller (see
 * pages/communityDetail/components/eventTracking/) — this module has no opinion on what it means.
 *
 * @module leadGenerators/services/googleAnalytics4EventBuilder
 */

/**
 * @param {object} params
 * @param {string} params.buttonName - 'book_appointment' | 'contact_sales_center'
 * @param {string} params.position
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildFormOpenEvent({ buttonName, position }) {
  return {
    eventName: 'form_open',
    customProperties: {
      button_name: buttonName,
      position,
    },
  };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {string} [params.date] - tour date, RequestATourForm only
 * @param {number} params.timeToSubmit - seconds
 * @param {string} [params.preferredContactMethod]
 * @param {string} [params.realtorQuestion]
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildGenerateLeadEvent({
  buttonName, position, date, timeToSubmit,
  preferredContactMethod, realtorQuestion,
}) {
  const customProperties = {
    button_name: buttonName,
    position,
    time_to_submit: timeToSubmit,
    preferd_method: preferredContactMethod,
    realtor_question: realtorQuestion,
  };

  if (date) {
    customProperties.lead_form_topics = date;
  }

  return { eventName: 'generate_lead', customProperties };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {'x_button'|'outside_click'|'escape'} params.closeTarget
 * @param {number} params.fieldsCompletedCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildFormCloseEvent({ buttonName, position, closeTarget, fieldsCompletedCount }) {
  return {
    eventName: 'form_close',
    customProperties: {
      button_name: buttonName,
      position,
      click_target: closeTarget,
      // click_target1 reflects whether the user actually entered data (fieldsCompletedCount > 0),
      // not just whether they focused a field — formStarted (leadFormTrackingState.js) goes true
      // on focus alone, so it can't answer "did they fill anything in".
      click_target1: fieldsCompletedCount > 0,
      click_target2: fieldsCompletedCount,
    },
  };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {number} params.timeSpent - seconds
 * @param {string|null} params.lastFieldInteracted
 * @param {boolean} params.hasValidationError
 * @param {'invalid_format'|'required_missing'} [params.errorType]
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLeadFormAbandonedEvent({
  buttonName, position, timeSpent, lastFieldInteracted,
  hasValidationError, errorType,
}) {
  const customProperties = {
    button_name: buttonName,
    position,
    time_spent: timeSpent,
  };

  if (lastFieldInteracted) {
    customProperties.click_target1 = `last_field_${lastFieldInteracted}`;
  }

  if (hasValidationError) {
    customProperties.click_target2 = `error_type_${errorType}`;
  }

  return { eventName: 'lead_form_abandoned', customProperties };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {string} params.errorType - 'validation'
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLeadFormSubmissionFailedEvent({ buttonName, position, errorType }) {
  return {
    eventName: 'lead_form_submission_failed',
    customProperties: {
      button_name: buttonName,
      position,
      click_target2: `error_type_${errorType}`,
    },
  };
}

/**
 * "Get on the list" (Coming Soon "Get Notified") event payload builders (NHS20-8254). These reuse
 * GA4's generic `native_map_events` name with `click_target`/`click_target1`/`click_target2` —
 * the same convention nativeInteractiveSiteMap/services/googleAnalytics4EventBuilder.js already
 * uses for its own events (e.g. buildNotifyWhenAvailableClickedEvent) — since the ticket's own
 * event vocabulary is already ISM-flavored, not a leadGenerators-specific one.
 */

/**
 * @param {object} params
 * @param {number} params.timeToSubmit - seconds
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifySubmitClickedEvent({ timeToSubmit }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'notify_submit_clicked',
      time_to_submit: timeToSubmit,
    },
  };
}

/**
 * @param {object} params
 * @param {string} params.errorType - 'validation'
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifySubmissionFailedEvent({ errorType }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'notify_submission_failed',
      click_target2: `error_type_${errorType}`,
    },
  };
}

/**
 * @param {object} params
 * @param {'x_button'|'outside_click'|'escape'} params.closeTarget
 * @param {boolean} params.emailEntered
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifyModalClosedEvent({ closeTarget, emailEntered }) {
  const customProperties = {
    click_target: 'notify_modal_closed',
    click_target1: closeTarget,
  };

  // Per the ticket: "blank if false" — GA4 custom properties are omitted, not sent as false.
  if (emailEntered) {
    customProperties.click_target2 = 'email_entered';
  }

  return { eventName: 'native_map_events', customProperties };
}

/**
 * @param {object} params
 * @param {boolean} params.emailFieldInteracted
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifyAbandonedEvent({ emailFieldInteracted }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'notify_abandoned',
      click_target1: emailFieldInteracted ? 'email_field_interacted' : 'email_field_not_interacted',
    },
  };
}
